import { Box, Flex, Heading, Spinner, Text } from '@chakra-ui/react';
import { useIsMutating } from '@tanstack/react-query';
import { FunctionComponent } from 'react';
import { DiffCodeEditor } from '@/components';
import { queryKeys } from '@/services/queryKeys';

type Props = {
  originalMeta: string;
  meta: string;
  onChange: (meta: string) => void;
};

const EmptyState: FunctionComponent = () => {
  const isGenerating = useIsMutating({ mutationKey: queryKeys.generateMeta }) > 0;

  return (
    <Flex h="100%" direction="column" align="center" justify="center" gap={3} p={6} textAlign="center">
      {isGenerating && <Spinner color="marketcast.primaryCta.1" thickness="3px" />}
      <Text fontSize="14px" color="marketcast.gray.2">
        {isGenerating
          ? 'Loading the current meta and processing it for this wave...'
          : 'Generate the meta to compare the current meta with the processed one.'}
      </Text>
    </Flex>
  );
};

/** Current meta (left, read-only) next to the processed meta (right, editable). */
export const MetaEditorPanel: FunctionComponent<Props> = ({ originalMeta, meta, onChange }) => {
  const hasMeta = meta.trim().length > 0;

  return (
    <Flex
      direction="column"
      h="100%"
      minH="480px"
      bg="marketcast.base.backgroundAlt"
      borderRadius="12px"
      border="1px solid"
      borderColor="marketcast.gray.5"
      boxShadow="sm"
      p={2}
      gap={2}
    >
      <Flex align="center" gap={3} wrap="wrap">
        <Heading as="h2" fontSize="16px" fontWeight="600" color="marketcast.text.primary">
          Dashboard meta
        </Heading>
      </Flex>

      <Box flex={1} minH={0}>
        {/* Editor mounts only once there's meta to compare. */}
        {hasMeta ? (
          <DiffCodeEditor
            original={originalMeta}
            modified={meta}
            onModifiedChange={onChange}
            language="json"
            originalLabel="Current meta (S3)"
            modifiedLabel="Processed meta (editable)"
            originalAriaLabel="Current dashboard meta JSON"
            modifiedAriaLabel="Processed dashboard meta JSON"
          />
        ) : (
          <Box h="100%" borderRadius="8px" border="1px solid" borderColor="marketcast.gray.5">
            <EmptyState />
          </Box>
        )}
      </Box>
    </Flex>
  );
};
